import { useMemo, useState } from 'react';
import { Button, Checkbox, TextArea } from '@trove/ui';
import { Check, Copy, DownloadSimple, Printer } from '@phosphor-icons/react';
import { createRecoveryMnemonic } from '@trove/crypto';
import { isUsableRecoveryPhrase, recoveryErrorMessage } from '../recoveryFeedback';
import type { PasskeyIcon } from '../passkeyIcons';
import { useWorkspace } from '../workspace';
import { EntryActions, EntryNotice } from './EntryLayout';
import styles from './VaultSetup.module.css';

/** Best-effort default name for a passkey created on this device. */
export function defaultPasskeyIdentity(): { label: string; icon: PasskeyIcon } {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (/iPhone/.test(agent)) return { label: 'iPhone', icon: 'smartphone' };
  if (/iPad/.test(agent)) return { label: 'iPad', icon: 'smartphone' };
  if (/Android/.test(agent)) return { label: 'Android', icon: 'smartphone' };
  if (/Macintosh|Mac OS X/.test(agent)) return { label: 'Mac', icon: 'laptop' };
  if (/Windows/.test(agent)) return { label: 'Windows PC', icon: 'laptop' };
  if (/Linux|CrOS/.test(agent)) return { label: 'Computer', icon: 'laptop' };
  return { label: 'This device', icon: 'fingerprint' };
}

/**
 * Shows a fresh backup phrase and creates the encrypted vault once the user has saved it.
 */
export function BackupPhraseStep({
  onCreated,
  onBack,
}: {
  onCreated: () => void | Promise<void>;
  onBack?: () => void;
}) {
  const { createRepository, syncing } = useWorkspace();
  const mnemonic = useMemo(() => createRecoveryMnemonic(), []);
  const words = useMemo(() => mnemonic.split(' '), [mnemonic]);
  const [saved, setSaved] = useState<'download' | 'copy' | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();

  const download = () => {
    const body = [
      'Trove backup phrase',
      '',
      mnemonic,
      '',
      'Keep this offline. Anyone with this phrase can unlock your files.',
      'Trove cannot reset it for you.',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'trove-backup.txt';
    link.click();
    URL.revokeObjectURL(url);
    setSaved('download');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(mnemonic);
      setCopied(true);
      setSaved((current) => current ?? 'copy');
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('Could not copy. Download the backup file instead.');
    }
  };

  const create = async () => {
    setError(undefined);
    setCreating(true);
    try {
      await createRepository(mnemonic, false);
      await onCreated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create your trove.');
      setCreating(false);
    }
  };

  const busy = creating || syncing;

  return (
    <>
      <div className={styles.phraseBox}>
        <ol className={styles.phrase} aria-label="Backup phrase">
          {words.map((word, index) => (
            <li key={`${word}-${index}`}>
              <span className={styles.index}>{index + 1}</span>
              <span className={styles.word}>{word}</span>
            </li>
          ))}
        </ol>
        <div className={styles.phraseTools}>
          <button type="button" className={styles.tool} onClick={download}>
            {saved === 'download' ? <Check size={14} /> : <DownloadSimple size={14} />}
            {saved === 'download' ? 'Downloaded' : 'Download'}
          </button>
          <button type="button" className={styles.tool} onClick={() => void copy()}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" className={styles.tool} onClick={() => window.print()}>
            <Printer size={14} />
            Print
          </button>
        </div>
      </div>

      <Checkbox
        label="I’ve saved my backup phrase somewhere safe"
        checked={confirmed}
        disabled={!saved || busy}
        onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
      />

      {error ? <EntryNotice tone="danger">{error}</EntryNotice> : null}

      <EntryActions>
        <Button
          variant="primary"
          disabled={!saved || !confirmed || busy}
          onClick={() => void create()}
        >
          {busy
            ? 'Creating your trove…'
            : !saved
              ? 'Download or copy to continue'
              : 'Create my trove'}
        </Button>
        {onBack ? (
          <Button variant="ghost" disabled={busy} onClick={onBack}>
            Back
          </Button>
        ) : null}
      </EntryActions>
    </>
  );
}

/** Unlocks an existing vault on this browser from the saved backup phrase. */
export function RestorePhraseForm({ onUnlocked }: { onUnlocked: () => void }) {
  const { restoreRepository, syncing } = useWorkspace();
  const [phrase, setPhrase] = useState('');
  const [error, setError] = useState<string>();

  const restore = async () => {
    setError(undefined);
    try {
      await restoreRepository(phrase.trim().replace(/\s+/g, ' '));
      onUnlocked();
    } catch (cause) {
      setError(recoveryErrorMessage(cause));
    }
  };

  return (
    <form
      className={styles.restore}
      onSubmit={(event) => {
        event.preventDefault();
        if (isUsableRecoveryPhrase(phrase) && !syncing) void restore();
      }}
    >
      <TextArea
        label="Backup phrase"
        value={phrase}
        onChange={(event) => {
          setPhrase(event.currentTarget.value);
          setError(undefined);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            event.currentTarget.form?.requestSubmit();
          }
        }}
        placeholder="word1 word2 word3 …"
        rows={4}
        autoFocus
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? 'recovery-phrase-error' : undefined}
      />
      {error ? (
        <div id="recovery-phrase-error">
          <EntryNotice tone="danger">{error}</EntryNotice>
        </div>
      ) : null}
      <EntryActions>
        <Button
          type="submit"
          variant="primary"
          disabled={!isUsableRecoveryPhrase(phrase) || syncing}
        >
          {syncing ? 'Unlocking…' : 'Unlock'}
        </Button>
      </EntryActions>
    </form>
  );
}
